import { useTheme } from '@/common/theme';
import './WoodCanvas.css';

/**
 * Wood "desk" canvas — an inline SVG wood layer rendered BEHIND the floating
 * paper sheets (chat thread, module panels). It is a real vector asset (crisp
 * at any size), not a CSS-gradient plank hack, and never intercepts clicks
 * (`pointer-events:none`).
 *
 * Two wood themes share the same layout but a different desk:
 *   • Nordic  — honey-blond pine: the original soft vertical-band canvas with
 *     two turbulence grain filters (`woodGrainSoft` / `woodGrainLine`).
 *   • Atelier — rich walnut craftsman's workbench: realistic horizontal grain
 *     (per-board gradients, grain-line + knot patterns, plank seams), ids
 *     namespaced `wood-atelier-*`. No id collisions with Nordic.
 *
 * Self-gating: renders only under those two themes (returns null otherwise),
 * so callers can mount it unconditionally. The canvas is rendered at opacity
 * 0.5 (nordic.css / atelier.css); both palettes are tuned for that softening.
 */
export function WoodCanvas() {
  const [theme] = useTheme();
  if (theme === 'atelier') return <AtelierWood />;
  if (theme === 'nordic') return <NordicWood />;
  return null;
}

/* ── Nordic — honey-blond pine desk ─────────── */
function NordicWood() {
  return (
    /* Wrapper carries the OPAQUE wood token; only the SVG is softened to 0.5
       opacity (themed css). The grain must composite over the token — over
       html/body's near-white paper it washes out to a milky tint. */
    <div className="wood-canvas">
    <svg
      className="wood-canvas-svg"
      viewBox="0 0 1300 1000"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="woodLight" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff6e2" stopOpacity="0.18" />
          <stop offset="50%" stopColor="#ffffff" stopOpacity="0.02" />
          <stop offset="100%" stopColor="#cba35c" stopOpacity="0.10" />
        </linearGradient>
        <filter id="woodGrainSoft" x="0" y="0" width="100%" height="100%">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.055 0.0022"
            numOctaves="4"
            seed="11"
            stitchTiles="stitch"
            result="n"
          />
          <feColorMatrix
            in="n"
            type="matrix"
            values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 1.1 0 -0.18"
            result="a"
          />
          <feComposite operator="in" in2="SourceGraphic" />
        </filter>
        <filter id="woodGrainLine" x="0" y="0" width="100%" height="100%">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.11 0.004"
            numOctaves="3"
            seed="29"
            stitchTiles="stitch"
            result="n"
          />
          <feColorMatrix
            in="n"
            type="matrix"
            values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 1.4 0 -0.55"
            result="a"
          />
          <feComposite operator="in" in2="SourceGraphic" />
        </filter>
      </defs>
      {/* wide, barely-different vertical bands (subtle plank structure, no dark seams) */}
      <rect x="0" y="0" width="230" height="1000" fill="#f6d4a1" />
      <rect x="230" y="0" width="290" height="1000" fill="#f1cd95" />
      <rect x="520" y="0" width="240" height="1000" fill="#f8d8a8" />
      <rect x="760" y="0" width="280" height="1000" fill="#f3cf99" />
      <rect x="1040" y="0" width="260" height="1000" fill="#f6d3a0" />
      <rect x="229" y="0" width="2" height="1000" fill="#fce3b8" opacity="0.30" />
      <rect x="519" y="0" width="2" height="1000" fill="#fce3b8" opacity="0.30" />
      <rect x="759" y="0" width="2" height="1000" fill="#fce3b8" opacity="0.30" />
      <rect x="1039" y="0" width="2" height="1000" fill="#fce3b8" opacity="0.30" />
      {/* grain */}
      <rect width="1300" height="1000" fill="#dcb16e" filter="url(#woodGrainSoft)" opacity="0.30" />
      <rect width="1300" height="1000" fill="#ca9d54" filter="url(#woodGrainLine)" opacity="0.18" />
      <rect width="1300" height="1000" fill="url(#woodLight)" />
    </svg>
    </div>
  );
}

/* ── Atelier — walnut / craftsman workbench (rich, deep grain) ────────────── */
function AtelierWood() {
  return (
    /* Same wrapper-token + softened-SVG composite as NordicWood above. */
    <div className="wood-canvas">
    <svg
      className="wood-canvas-svg"
      viewBox="0 0 1300 1000"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {/* Continuous single-slab walnut base. All variation is diagonal /
            lengthwise (x-axis) / radial — never a horizontal (y-axis) stop — so
            no plank band or seam can render. Replaces the old stacked boards. */}
        <linearGradient id="wood-atelier-diagonal-light" x1="0" y1="0" x2="1" y2="1" gradientUnits="objectBoundingBox">
          <stop offset="0" stopColor="#7a4a2c" />
          <stop offset=".34" stopColor="#5e3620" />
          <stop offset=".68" stopColor="#512d1d" />
          <stop offset="1" stopColor="#4a2814" />
        </linearGradient>
        <linearGradient id="wood-atelier-lengthwise" x1="0" y1="0" x2="1" y2="0" gradientUnits="objectBoundingBox">
          <stop offset="0" stopColor="#5a311f" stopOpacity=".55" />
          <stop offset=".26" stopColor="#7d4b2d" stopOpacity=".30" />
          <stop offset=".5" stopColor="#4d2819" stopOpacity=".42" />
          <stop offset=".74" stopColor="#6e4226" stopOpacity=".28" />
          <stop offset="1" stopColor="#46260f" stopOpacity=".55" />
        </linearGradient>
        <radialGradient id="wood-atelier-figure-bloom" cx="38%" cy="40%" r="82%" gradientUnits="objectBoundingBox">
          <stop offset="0" stopColor="#8c5734" stopOpacity=".34" />
          <stop offset=".46" stopColor="#6e4226" stopOpacity=".14" />
          <stop offset="1" stopColor="#3c1f10" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="wood-atelier-warm-pool" cx="78%" cy="80%" r="56%" gradientUnits="objectBoundingBox">
          <stop offset="0" stopColor="#80502f" stopOpacity=".22" />
          <stop offset=".55" stopColor="#5e3620" stopOpacity=".08" />
          <stop offset="1" stopColor="#46260f" stopOpacity="0" />
        </radialGradient>

        <radialGradient id="wood-atelier-soft-glow" cx="24%" cy="10%" r="88%">
          <stop offset="0" stopColor="#c98a55" stopOpacity=".38" />
          <stop offset=".48" stopColor="#9d6039" stopOpacity=".16" />
          <stop offset="1" stopColor="#2a140c" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="wood-atelier-edge-shade" cx="50%" cy="45%" r="72%">
          <stop offset=".54" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="1" stopColor="#170a06" stopOpacity=".44" />
        </radialGradient>

        <filter
          id="wood-atelier-long-grain"
          x="0"
          y="0"
          width="1300"
          height="1000"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.0035 0.068"
            numOctaves="3"
            seed="41"
            stitchTiles="stitch"
            result="noise"
          />
          <feColorMatrix
            in="noise"
            type="matrix"
            values="0 0 0 0 0.13 0 0 0 0 0.07 0 0 0 0 0.04 0.66 0.66 0.66 0 -0.72"
            result="grain"
          />
          <feComposite in="grain" in2="SourceAlpha" operator="in" />
        </filter>
        <filter
          id="wood-atelier-fine-grain"
          x="0"
          y="0"
          width="1300"
          height="1000"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.016 0.24"
            numOctaves="2"
            seed="53"
            stitchTiles="stitch"
            result="noise"
          />
          <feColorMatrix
            in="noise"
            type="matrix"
            values="0 0 0 0 0.08 0 0 0 0 0.04 0 0 0 0 0.025 0.46 0.46 0.46 0 -0.48"
            result="grain"
          />
          <feComposite in="grain" in2="SourceAlpha" operator="in" />
        </filter>

        <pattern id="wood-atelier-grain-lines" patternUnits="userSpaceOnUse" width="1300" height="1000">
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M-80 38 C 100 30 182 45 330 37 S 560 30 740 43 S 1050 55 1380 35" stroke="#24110a" strokeOpacity=".46" strokeWidth="1.6" />
            <path d="M-60 72 C 135 83 260 63 418 75 C 588 88 700 66 870 78 S 1160 98 1370 70" stroke="#a46a3f" strokeOpacity=".18" strokeWidth="2.2" />
            <path d="M20 112 C 188 102 265 123 410 113 S 670 94 810 116 S 1090 130 1260 108" stroke="#2d160d" strokeOpacity=".42" strokeWidth="1.2" />
            <path d="M-40 144 C 160 135 295 152 475 142 S 780 130 980 148 S 1180 160 1360 144" stroke="#c78a55" strokeOpacity=".13" strokeWidth="2.4" />
            <path d="M35 188 C 210 178 330 197 510 186 C 690 174 825 200 990 188 S 1185 176 1325 190" stroke="#1d0d08" strokeOpacity=".44" strokeWidth="1.7" />
            <path d="M-70 238 C 110 225 270 246 440 232 S 720 215 915 236 S 1120 255 1375 230" stroke="#3a1d11" strokeOpacity=".36" strokeWidth="2.1" />
            <path d="M60 292 C 230 252 395 253 560 292 C 705 326 855 328 1040 288 C 1150 265 1238 270 1350 292" stroke="#2a140c" strokeOpacity=".42" strokeWidth="2.8" />
            <path d="M90 314 C 260 284 408 287 552 315 C 690 342 850 342 1014 314" stroke="#140907" strokeOpacity=".36" strokeWidth="1.3" />
            <path d="M-80 356 C 120 344 238 366 430 352 S 760 336 930 356 S 1140 374 1380 352" stroke="#211009" strokeOpacity=".48" strokeWidth="1.5" />
            <path d="M25 402 C 220 394 342 410 515 399 S 735 388 900 405 S 1130 420 1280 398" stroke="#bc7a49" strokeOpacity=".13" strokeWidth="2.6" />
            <path d="M-60 455 C 115 466 270 442 438 458 C 588 472 720 446 872 460 S 1100 480 1370 452" stroke="#2f170d" strokeOpacity=".44" strokeWidth="1.8" />
            <path d="M70 492 C 235 485 360 505 540 494 S 790 478 960 498 S 1168 514 1335 492" stroke="#9d6038" strokeOpacity=".17" strokeWidth="1.7" />
            <path d="M-90 548 C 100 535 250 560 435 545 S 715 528 925 550 S 1130 570 1390 540" stroke="#180a06" strokeOpacity=".42" strokeWidth="1.4" />
            <path d="M35 598 C 180 584 330 610 505 596 S 730 578 900 600 S 1110 620 1290 596" stroke="#a96d42" strokeOpacity=".17" strokeWidth="2.7" />
            <path d="M-40 642 C 155 632 280 650 450 640 S 720 628 890 646 S 1130 660 1360 638" stroke="#26120b" strokeOpacity=".48" strokeWidth="1.4" />
            <path d="M75 690 C 255 650 425 653 585 692 C 735 728 870 728 1040 688 C 1165 658 1245 665 1360 690" stroke="#2a140c" strokeOpacity=".46" strokeWidth="2.8" />
            <path d="M95 715 C 250 690 420 692 575 718 C 722 742 870 742 1025 716" stroke="#130806" strokeOpacity=".38" strokeWidth="1.3" />
            <path d="M-70 765 C 120 755 270 776 442 762 S 735 748 925 768 S 1125 785 1375 760" stroke="#221009" strokeOpacity=".50" strokeWidth="1.6" />
            <path d="M20 812 C 190 798 330 824 510 810 S 735 792 910 815 S 1120 832 1290 810" stroke="#c88b57" strokeOpacity=".12" strokeWidth="2.7" />
            <path d="M-40 858 C 140 870 300 846 470 860 C 635 875 755 850 930 862 S 1145 884 1360 854" stroke="#1c0d08" strokeOpacity=".44" strokeWidth="1.5" />
            <path d="M65 910 C 240 900 360 922 535 910 S 805 895 980 914 S 1180 930 1335 910" stroke="#935936" strokeOpacity=".18" strokeWidth="2" />
            <path d="M-80 958 C 120 944 275 970 455 956 S 740 938 925 960 S 1140 980 1385 952" stroke="#211009" strokeOpacity=".48" strokeWidth="1.5" />
          </g>
        </pattern>

        <pattern id="wood-atelier-character" patternUnits="userSpaceOnUse" width="1300" height="1000">
          <g fill="none" strokeLinecap="round" strokeLinejoin="round">
            <ellipse cx="360" cy="258" rx="64" ry="16" transform="rotate(-3 360 258)" stroke="#1a0c07" strokeOpacity=".46" strokeWidth="2.8" />
            <ellipse cx="360" cy="258" rx="34" ry="8" transform="rotate(-3 360 258)" stroke="#9b6038" strokeOpacity=".24" strokeWidth="1.5" />
            <path d="M120 255 C 215 239 300 244 342 257 C 395 274 465 273 565 252 C 650 235 720 240 795 255" stroke="#24110a" strokeOpacity=".38" strokeWidth="1.7" />
            <ellipse cx="930" cy="694" rx="78" ry="19" transform="rotate(2 930 694)" stroke="#180a06" strokeOpacity=".42" strokeWidth="3" />
            <ellipse cx="930" cy="694" rx="44" ry="9" transform="rotate(2 930 694)" stroke="#a3643b" strokeOpacity=".20" strokeWidth="1.6" />
            <path d="M655 690 C 760 674 850 678 910 694 C 978 712 1070 711 1205 684" stroke="#211009" strokeOpacity=".38" strokeWidth="1.8" />
            <ellipse cx="215" cy="832" rx="46" ry="12" transform="rotate(-4 215 832)" stroke="#1b0d08" strokeOpacity=".34" strokeWidth="2.3" />
            <path d="M-30 832 C 70 820 160 821 205 832 C 260 846 350 844 455 825" stroke="#24110a" strokeOpacity=".32" strokeWidth="1.5" />
            <path d="M725 160 C 830 122 960 122 1070 158 C 1155 186 1230 184 1340 154" stroke="#c98a55" strokeOpacity=".12" strokeWidth="3.4" />
            <path d="M730 180 C 835 145 955 146 1060 180 C 1150 209 1238 205 1360 174" stroke="#1f0f09" strokeOpacity=".34" strokeWidth="1.7" />
          </g>
        </pattern>
      </defs>

      {/* One continuous walnut slab — full-canvas layers only (no y-offset/thin
          rects), so the desk reads as a single edge-to-edge board. */}
      <rect width="1300" height="1000" fill="#4d2819" />
      <rect width="1300" height="1000" fill="url(#wood-atelier-diagonal-light)" />
      <rect width="1300" height="1000" fill="url(#wood-atelier-lengthwise)" opacity=".7" />
      <rect width="1300" height="1000" fill="url(#wood-atelier-figure-bloom)" />
      <rect width="1300" height="1000" fill="url(#wood-atelier-warm-pool)" />
      <rect width="1300" height="1000" fill="#6e4226" filter="url(#wood-atelier-fine-grain)" opacity=".16" />

      <rect width="1300" height="1000" fill="#000000" filter="url(#wood-atelier-long-grain)" opacity=".52" />
      <rect width="1300" height="1000" fill="#000000" filter="url(#wood-atelier-fine-grain)" opacity=".34" />

      <rect width="1300" height="1000" fill="url(#wood-atelier-grain-lines)" opacity=".96" />
      <rect width="1300" height="1000" fill="url(#wood-atelier-character)" opacity=".96" />

      <rect width="1300" height="1000" fill="url(#wood-atelier-soft-glow)" opacity=".44" />
      <rect width="1300" height="1000" fill="url(#wood-atelier-edge-shade)" opacity=".48" />
    </svg>
    </div>
  );
}

export default WoodCanvas;
